import { CSSProperties } from 'react';
import { toastMessage } from '@croquiscom-pvt/content-ui';

import { useBottomDialog } from '@common/overlay-manager';

import { RemoveItemDialog } from '../components/RemoveItemDialog';
import { useOptionContext } from '../context/OptionProvider';
import { PdpCatalogFulfillmentType } from '../graphql/select-option_types.2';
import { useMinimumOrderQuantityToastMessage } from '../hooks/useMinimumOrderQuantityToastMessage';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { useOptions } from '../queries/useOptions';
import { OptionType, SelectedItem } from '../types/option-type';
import { useCommonService } from './useCommonService';

export const useItemCardsService = ({
  allowAnyQuantity,
  disabledCounter,
}: {
  allowAnyQuantity?: boolean;
  disabledCounter?: boolean;
}) => {
  const { entry_source_type } = useOptionContext();
  const { open: openBottomSheet, close: closeBottomSheet } = useBottomDialog();
  const { itemCards, itemCardTotalCount, isNoOption, removeItem, changeItemCounts } = useOptionsControl();
  const { isOrderQuantityItemType, isOrderQuantityProductType } = useCommonService();
  const {
    options: {
      catalog_product: {
        minimum_order_quantity,
        minimum_order_quantity_type,
        product_price: { first_order_discount },
        fulfillment_type,
        store_deal_banner,
        promotion_info,
      },
      flags: { is_purchase_only_one_at_time },
    },
  } = useOptions();

  const bogoImageUrl = entry_source_type === 'EXCHANGE' ? '' : promotion_info?.bogo_info?.badge?.image_url;

  const has_first_order_discount = Boolean(first_order_discount?.discount_amount);

  const minOrderQuantity = allowAnyQuantity ? 1 : minimum_order_quantity;

  const orderQuantityToastMessage = useMinimumOrderQuantityToastMessage(minimum_order_quantity_type, minOrderQuantity);

  const hasOrderQuantityItemType = isOrderQuantityItemType && minOrderQuantity;

  const handleChangeItemCount = (item: SelectedItem, count: number) => {
    const should_show_toast =
      hasOrderQuantityItemType &&
      minOrderQuantity > 0 &&
      count < minOrderQuantity &&
      item.option_type !== OptionType.ADDITIONAL;

    if (should_show_toast) {
      const toast_style: CSSProperties = {
        position: 'relative',
        bottom: '60px',
        maxWidth: 560,
        margin: '0 auto',
      };
      toastMessage(orderQuantityToastMessage, undefined, { toast_style });
      changeItemCounts({ item, count: minOrderQuantity });
      return;
    }

    changeItemCounts({ item, count });
  };

  const handleItemCardRemove = (item: SelectedItem) => () => {
    const is_last_item = itemCards.filter((o) => o.item.option_type !== OptionType.ADDITIONAL).length === 1;
    const has_additional_items = itemCards.find((o) => o.item.option_type === OptionType.ADDITIONAL);
    const visible_alert = is_last_item && has_additional_items && item.option_type !== OptionType.ADDITIONAL;
    const removeAllItems = () => {
      itemCards.forEach(({ item }) => {
        removeItem(item);
      });
    };

    if (visible_alert) {
      return openBottomSheet({
        children: (
          <RemoveItemDialog
            onRemove={() => {
              removeAllItems();
              closeBottomSheet();
            }}
            onCancel={() => closeBottomSheet()}
          />
        ),
      });
    }

    removeItem(item);
  };

  function getMinCount(item: SelectedItem, minCount: number) {
    const other_items_count =
      itemCardTotalCount > item.count ? itemCardTotalCount - item.count : item.count - itemCardTotalCount;
    const required_quantity = other_items_count > minOrderQuantity ? 1 : minOrderQuantity - other_items_count;
    if (item.option_type !== OptionType.ADDITIONAL && isOrderQuantityProductType) {
      return required_quantity === 0 ? 1 : required_quantity;
    }

    return item.option_type !== OptionType.ADDITIONAL && minOrderQuantity && minOrderQuantity > minCount
      ? minOrderQuantity
      : minCount;
  }

  function getMaxCount(item: SelectedItem, maxCount: number) {
    const is_zigzin = fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN;
    // MEME: 매칭 옵션의 경우는 제외하기 위해 같은 상품 타입인지 확인
    const same_product_type_option = is_zigzin === item.is_zigzin;

    if (has_first_order_discount && same_product_type_option) {
      return 1;
    }

    return maxCount;
  }

  const disableMinCounter = (count: number) => {
    return isOrderQuantityProductType && itemCardTotalCount > minOrderQuantity && count === 1;
  };

  const getOverMaxMessage = () => {
    if (has_first_order_discount) {
      return '첫구매 상품은 1개까지만 구매 가능합니다';
    }

    if (store_deal_banner) {
      return '100원딜 상품은 1개까지만 구매 가능합니다';
    }
    return '';
  };

  return {
    itemCards,
    bogoImageUrl,
    isNoOption,
    over_max_message: getOverMaxMessage(),
    orderQuantityToastMessage,
    handleChangeItemCount,
    handleItemCardRemove,
    getMinCount,
    getMaxCount,
    disableMinCounter,
    isOrderQuantityItemType,
    disableCounter: Boolean(is_purchase_only_one_at_time || disabledCounter || store_deal_banner),
  };
};
